import {
  TeButton,
  TeContainer,
  TeContainerSlim,
  TeDropdown,
  TeDropdownOption,
  TeForm,
  TeInputText,
  TeLabel,
  TeModal,
  TeText,
} from '@topcon/tuicl-react';
import { useEffect, useState } from 'react';
import { submitFormWithName } from '@topcon/tuicl/dist/components/form/form.utils';
import { Statuses } from '@topcon/tuicl/dist/common/component-types';
import { useTranslation } from 'react-i18next';
import { useNotification } from '@topcon/topcon-utils';
import { TeChangeEvent } from '@topcon/tuicl';

import { validateDomain } from '../../../utils/validation';
import { useUpdateDomain, useAddDomain } from './domainRegistration.mutations';
import { useDomain } from './domainRegistration.queries';
import { buildDomainPayload } from './domainRegistration.payload';
import { useIdentityProviders } from '../IdentityProviders/identityProviders.queries';
import { DomainBody } from '../../../api/generated/pap';
import { useErrorDrawer } from '../../../providers/ErrorDrawerContext/error-drawer.context';

interface Props {
  domain: DomainBody | null;
  open: boolean;
  onClose: (shouldUpdate: boolean) => void;
}

enum FormKeys {
  Name = 'name',
  IdentityProviderId = 'identityProviderId',
}

const { Name, IdentityProviderId } = FormKeys;

const FORM_NAME = 'DomainForm';

function AddEditDomainModal({ domain, open, onClose }: Props) {
  const { data: identityProviders, isFetched: loadedIdentityProviders } = useIdentityProviders();
  const notificationService = useNotification();
  const { showError } = useErrorDrawer();
  const isEdit = !!domain;
  const {
    id,
    name: defaultName = '',
    identityProviderId: defaultIdentityProviderId = '',
  } = domain ?? {};
  const [identityProviderId, setIdentityProviderId] = useState('');
  const [name, setName] = useState(defaultName);
  const [identityProviderIdTouch, setIdentityProviderIdTouch] = useState(false);
  const [nameTouch, setNameTouch] = useState(false);
  const { t } = useTranslation('settings');
  useDomain(open ? id : undefined);
  const { mutateAsync: updateDomain } = useUpdateDomain();
  const { mutateAsync: addDomain } = useAddDomain();
  const nameValid = validateDomain(name);
  const formValid = isEdit ? identityProviderIdTouch : nameValid;
  const onModalClose = (shouldUpdate: boolean) => {
    onClose(shouldUpdate);
    setName(defaultName);
    setIdentityProviderId(defaultIdentityProviderId);
    setIdentityProviderIdTouch(false);
    setNameTouch(false);
  };

  // All notification texts
  const notificationTexts: string[] = [
    t('domainRegistration.addFailure'),
    t('domainRegistration.addFailureDesc', { name }),
    t('domainRegistration.editFailure'),
    t('domainRegistration.editFailureDesc', { name }),
  ];

  const modalTitle = isEdit
    ? t('domainRegistration.editDomain')
    : t('domainRegistration.addDomain');
  const confirmText = isEdit
    ? t('domainRegistration.saveChanges')
    : t('domainRegistration.addDomain');

  const showFailureNotification = () => {
    const operation = isEdit ? 'edit' : 'add';
    const header = t(`domainRegistration.${operation}Failure`);
    const desc = t(`domainRegistration.${operation}FailureDesc`, { name });
    if (!notificationTexts.includes(header)) return;
    notificationService.addNotification(header, desc, Statuses.error);
  };

  const performOperation = async (): Promise<'success' | 'failed' | 'handled'> => {
    const payload = buildDomainPayload({
      isEdit,
      name: isEdit ? defaultName : name,
      identityProviderId,
    });
    try {
      if (id) await updateDomain({ id, payload });
      else await addDomain(payload);
      return 'success';
    } catch (error: unknown) {
      console.error('Operation failed', error);
      if (showError(error)) {
        return 'handled';
      }
      return 'failed';
    }
  };

  const handleFormSubmit = async () => {
    const result = await performOperation();
    if (result === 'failed') {
      showFailureNotification();
    }
    onModalClose(result === 'success');
  };

  const handleTextInputChange = (e: TeChangeEvent) => {
    const newValue = e.target.value.trim();
    setName(newValue);
    setNameTouch((prev) => prev || newValue !== name);
  };

  useEffect(() => {
    setName(defaultName);
    setIdentityProviderId(defaultIdentityProviderId);
  }, [defaultName, defaultIdentityProviderId]);

  return (
    <TeModal
      variant="dark"
      open={open}
      closeMode="button"
      teclose={() => onModalClose(false)}
      modalTitle={modalTitle}
      contentPadding={0}
      closeable
      size="s"
      width="s"
    >
      <TeContainer backgroundColor="background.primary" padding={['m', 's']} column grow>
        <TeForm name={FORM_NAME} tesubmit={handleFormSubmit}>
          <TeContainerSlim gap="xs" column fill>
            <TeLabel text={t('domainRegistration.domainName')} />
            <TeInputText
              name={Name}
              grow
              size="s"
              readonly={isEdit}
              status={isEdit || !nameTouch || nameValid ? Statuses.neutral : Statuses.error}
              valueByDefault={defaultName}
              value={isEdit ? defaultName : name}
              techange={handleTextInputChange}
              placeholder={t('domainRegistration.domainNamePlaceholder')}
            />
            {nameTouch && !nameValid && (
              <TeText color="text.error.primary" size="s">
                {t('domainRegistration.enterValidDomain')}
              </TeText>
            )}
          </TeContainerSlim>

          <TeContainerSlim gap="xs" column fill>
            <TeLabel text={t('domainRegistration.identityProvider')} />
            <TeDropdown
              name={IdentityProviderId}
              valueByDefault={identityProviderId}
              value={identityProviderId}
              grow
              size="s"
              fixedPosition
              placeholderText={t('domainRegistration.identityProviderPlaceholder')}
              persistent={false}
              readonly={!loadedIdentityProviders}
              techange={(e) => {
                setIdentityProviderIdTouch(true);
                setIdentityProviderId(e.value);
              }}
            >
              {identityProviders?.items.map(({ id, name }) => {
                return <TeDropdownOption key={id} value={id} text={name} />;
              })}
            </TeDropdown>
          </TeContainerSlim>
        </TeForm>
      </TeContainer>

      <TeButton
        slot="tertiary"
        text={t('cancel')}
        variant="neutral"
        teclick={() => onModalClose(false)}
        size="s"
      />
      <TeButton
        slot="primary"
        text={confirmText}
        variant="primary"
        teclick={() => submitFormWithName(FORM_NAME)}
        size="s"
        disabled={!formValid}
      />
    </TeModal>
  );
}

export default AddEditDomainModal;
